/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Delete elements by the given resource id.
 *
 * Contrary to deleteElements this works across all pages, not just the current one.
 *
 * If an empty id or a no matches with id, state is unchanged.
 *
 * When a pending placement id is given, only elements whose resource still
 * carries that pending placement are deleted: the element a placement inserted,
 * and any copy of it made while it was loading. Other elements using the same
 * resource are kept.
 *
 * @param {Object} state Current state
 * @param {Object} payload Action payload
 * @param {string|null} payload.id id Delete all elements with this resource id
 * @param {string} [payload.pendingPlacementId] Limit the deletion to this placement
 * @return {Object} New state
 */
function deleteElementsByResourceId(state, { id, pendingPlacementId }) {
  if (id === null) {
    return state;
  }

  const idsToDelete = [];
  const matches = (element) =>
    element?.resource?.id === id &&
    (pendingPlacementId === undefined ||
      element.resource.pendingPlacementId === pendingPlacementId);

  const newPages = state.pages.map((page) => {
    const { elements, animations } = page;

    const isDeletingBackground = matches(elements[0]);
    const backgroundIsDefault = elements[0].isDefaultBackground;

    let newElements = elements.map((element) => {
      const { id: elementId } = element;
      if (!matches(element)) {
        return element;
      }

      if (isDeletingBackground && backgroundIsDefault) {
        return element;
      }

      idsToDelete.push(elementId);

      return undefined;
    });

    if (isDeletingBackground && !backgroundIsDefault) {
      newElements = [page.defaultBackgroundElement, ...newElements];
    }

    newElements = newElements.filter(Boolean);

    // Remove animations associated with elements
    const oldAnimations = animations || [];
    const newAnimations = oldAnimations.filter((anim) =>
      anim.targets.some((elementId) => !idsToDelete.includes(elementId))
    );

    return {
      ...page,
      elements: newElements,
      ...(oldAnimations.length > 0 ? { animations: newAnimations } : {}),
    };
  });

  const newSelection = state.selection.filter(
    (selectedId) => !idsToDelete.includes(selectedId)
  );

  return {
    ...state,
    pages: newPages,
    selection: newSelection,
  };
}

export default deleteElementsByResourceId;
